<template>
  <div :class="$options.name">
    <v-dialog
      v-model="dialog"
      :loading="isLoading"
      max-width="600"
      scrollable
      @click:outside="close"
      @keydown.esc="close"
    >
      <v-card>
        <v-card-title>
          {{ title }}
        </v-card-title>

        <v-card-text>
          <h4 v-if="!isRegionData">Расположение</h4>
          <select-point-dialog
            v-if="mapType === 'point' && dialog"
            :color="form.geometry ? 'success' : 'primary'"
            :label="selectPointLabel"
            :geometry="form.geometry"
            @change="form.geometry = $event"
          />
          <map-polygon-editor-dialog
            v-else-if="['polygon', 'linestring'].includes(mapType) && dialog"
            :color="form.geometry ? 'success' : 'primary'"
            :label="mapPolygonLabel"
            :map-type="mapType"
            :geometry="form.geometry"
            @change="onPolygonEditorChange"
          />
          <div class="mt-2 mb-2" />
          <h4 v-if="!isRegionData">Данные об элементе</h4>
          <template v-for="(input, inputIndex) in form.properties">
            <div v-if="input.label !== 'Регион'" :key="inputIndex">
              <v-text-field
                v-if="input.type !== 'select'"
                v-model="form.properties[inputIndex].value"
                item-text="name"
                item-value="id"
                :label="input.name"
              />
              <v-select
                v-else
                v-model="form.properties[inputIndex].value"
                item-text="name"
                item-value="id"
                :items="input.preparedValues"
                :label="input.name"
              />
            </div>
          </template>

          <h4 v-if="!isRegionData">Дата и время события</h4>
          <v-row v-if="!isRegionData">
            <v-col md="6" class="pt-0">
              <v-menu
                ref="menu1"
                v-model="dateMenuState"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="dateValue"
                    label="Дата события"
                    persistent-hint
                    prepend-icon="event"
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="dateValue"
                  locale="ru"
                  no-title
                  :first-day-of-week="1"
                  @input="dateMenuState = false"
                />
              </v-menu>
            </v-col>

            <v-col md="6" class="pt-0">
              <v-menu
                ref="menu"
                v-model="timeMenuState"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="timeValue"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="timeValue"
                    label="Время события"
                    prepend-icon="access_time"
                    readonly
                    v-on="on"
                  />
                </template>
                <v-time-picker
                  v-if="timeMenuState"
                  v-model="timeValue"
                  format="24hr"
                  full-width
                  @click:minute="$refs.menu.save(timeValue)"
                />
              </v-menu>
            </v-col>
          </v-row>

          <div v-if="isRegionData">
            <v-text-field
              v-model="form.value"
              item-text="name"
              item-value="id"
              label="Значение"
            />
          </div>

          <div v-if="attachments.length">
            <h4>Приложения</h4>
            <ul>
              <li v-for="( attachment, index ) in attachments" :key="index">
                <a @click="openAttachmentFile(attachment.path)">{{ attachment.name }}</a>

                <a class="ml-5" @click="addAttachmentToRemove(index)">
                  <v-icon title="Удалить" color="red" small>
                    close
                  </v-icon>
                </a>
              </li>
            </ul>
          </div>

          <div>
            <v-file-input
              v-model="attachmentsToUpload"
              label="Прикрепить файл"
              multiple
              counter
              show-size
            />
          </div>

          <div v-if="form.userName && form.userEmail">
            <h4>Последнее изменение</h4>
            <v-row>
              <v-col md="6" class="pt-0">
                <v-text-field
                  v-model="form.userName"
                  label="Имя пользователя"
                  readonly
                  disabled
                />

                <v-text-field
                  v-if="form.updatedAt"
                  v-model="form.updatedAt"
                  label="Дата изменения"
                  readonly
                  disabled
                />
              </v-col>

              <v-col md="6" class="pt-0">
                <v-text-field
                  v-model="form.userEmail"
                  label="Почта"
                  readonly
                  disabled
                />
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <v-card-actions>
          <div class="flex-grow-1" />
          <v-btn color="error" text @click="close">Отмена</v-btn>
          <v-btn
            color="success"
            :disabled="!canSave"
            :loading="isLoading"
            text
            @click="save"
          >
            {{ saveButtonText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment';
import { openFile } from '@/utils/download-file';
import DialogBusMixin from '@/components/dialogs/mixins/dialog-bus-mixin';
import SelectPointDialog from './map/SelectPointDialog.vue';
import MapPolygonEditorDialog from './map/editor/MapPolygonEditorDialog.vue';

const createDefaultForm = () => ({
  geometry: null,
  properties: [],
  happenedAt: null,
});

const createDefaultRegionDataForm = () => ({
  regionDataId: null,
  value: null,
});

const createDefaultData = () => ({
  dialog: false,
  form: createDefaultForm(),
  isLoading: false,
  isEdit: false,
  isEditMany: false,
  itemId: null,
  dateMenuState: false,
  timeMenuState: false,
  timeValue: null,
  dateValue: null,
  isRegionData: false,
  attachmentsToUpload: [],
  attachmentsToRemove: [],
  attachments: [],
});

export default {
  name: 'new-object-geometry-dialog',
  components: { SelectPointDialog, MapPolygonEditorDialog },
  mixins: [DialogBusMixin],
  props: {
    propertyKeys: {
      type: Array,
      required: true,
    },
    objectType: {
      type: String,
      required: true,
    },
    objectId: {
      type: String,
      required: true,
    },
    mapType: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    ...createDefaultData(),
  }),
  computed: {
    mapPolygonLabel() {
      const { mapType } = this;
      const { geometry } = this.form;
      if (mapType === 'linestring') {
        return geometry ? 'Изменить путь' : 'Указать путь в редакторе';
      }
      return geometry ? 'Изменить область' : 'Указать область в редакторе';
    },
    title() {
      if (this.isEditMany) {
        return 'Редактирование элементов';
      }
      if (this.isEdit) {
        return 'Редактирование элемента';
      }
      return 'Новый элемент';
    },
    saveButtonText() {
      if (this.isEditMany) {
        return 'Обновить элементы';
      }
      if (this.isEdit) {
        return 'Обновить элемент';
      }
      return 'Создать элемент';
    },
    canSave() {
      return this.isEditMany || !!this.form.geometry || !!this.isRegionData;
    },
    selectPointLabel() {
      return (this.form.geometry && this.form.geometry.coordinates) ? `${this.form.geometry.coordinates.slice().reverse().join(', ')} (Ш,Д)` : 'Указать на карте';
    },
  },
  watch: {
    // dialog: 'updateData',
    dateValue() {
      this.setHappenedAt();
    },
    timeValue() {
      this.setHappenedAt();
    },
  },
  methods: {
    ...mapActions({
      update(dispatch, payload) {
        if (this.isRegionData) {
          return dispatch('layer-objects/geometries/updateRegionData', {
            objectId: payload.objectId,
            id: payload.regionDataId,
            value: payload.value,
          });
        }
        return dispatch('layer-objects/geometries/update', payload);
      },
      updateMany(dispatch, payload) {
        return dispatch('layer-objects/geometries/updateMany', payload);
      },
    }),
    ...mapActions(['showNotify']),
    ...mapActions('layer-objects', ['uploadGeometryAttachment', 'removeGeometryAttachment']),

    setHappenedAt() {
      if (this.dateValue && this.timeValue) {
        this.form.happenedAt = `${this.dateValue} ${this.timeValue}`;
      } else {
        this.form.happenedAt = null;
      }
    },

    beforeOpen({
      isEdit = false, isEditMany = false, item, items = [], mapType,
    } = {}) {
      this.dateValue = null;
      this.timeValue = null;
      this.isEdit = isEdit;
      this.isEditMany = isEditMany;
      this.isLoading = false;
      this.attachmentsToRemove = [];
      this.attachmentsToUpload = [];

      this.isRegionData = mapType === 'region';

      this.form = this.isRegionData ? createDefaultRegionDataForm() : createDefaultForm();

      if (this.isEdit && !this.isRegionData) {
        this.form.geometry = this.newObj(item.location);
        this.form.properties = item.properties.map(
          (element) => ({ ...element.field, value: element.value }),
        );

        // eslint-disable-next-line no-restricted-syntax
        for (const key of this.propertyKeys) {
          const index = this.form.properties.findIndex((prop) => prop.name === key.name);

          if (index === -1) {
            this.form.properties.push({
              name: key.name,
              type: key.type,
              preparedValues: key.preparedValues,
              id: key.id,
              value: '',
            });
          }
        }

        if (item.happenedAt) {
          this.dateValue = moment(item.happenedAt.date).format('YYYY-MM-DD');
          this.timeValue = moment(item.happenedAt.date).format('HH:mm:ss');
          this.setHappenedAt();
        }

        if (item.updatedBy) {
          this.form.userName = item.updatedBy.name;
          this.form.userEmail = item.updatedBy.email;
        }

        if (item.updatedAt) {
          this.form.updatedAt = moment(item.updatedAt.date).format('DD.MM.YYYY HH:mm');
        }

        if (item.attachments?.length) {
          this.attachments = [...item.attachments];
        }

        this.itemId = item.id;
      } else if (this.isEdit) {
        this.form.regionDataId = item.id;
        this.form.value = item.value;
      } else if (this.isEditMany) {
        this.itemId = items.map(_ => _.id);
        this.createProperties();
      } else {
        this.createProperties();
      }
    },

    openAttachmentFile(attachmentPath) {
      openFile(attachmentPath);
    },

    close() {
      this.$dialog.hide('new-object-geometry-dialog');
    },

    beforeClose() {
      const defaultData = createDefaultData();

      Object.keys(defaultData).forEach((key) => {
        this[key] = defaultData[key];
      });
    },

    createProperties() {
      this.form.properties = this.propertyKeys.map(propItem => ({
        name: propItem.name,
        type: propItem.type,
        preparedValues: propItem.preparedValues,
        id: propItem.id,
        value: '',
      }));
    },

    async updateObjectGeometry() {
      const properties = {};
      this.form.properties.forEach((element) => {
        properties[element.id] = element.value;
      });

      const params = {
        ...this.form,
        objectId: this.objectId,
        properties,
      };


      try {
        let message;
        if (this.isEdit) {
          const result = await this.update({ ...params, id: this.itemId });
          if (result) {
            message = 'Элемент слоя успешно обновлен';
          } else {
            throw new Error('Ошибка при обновлении элемента');
          }
        } else if (this.isEditMany) {
          message = 'Элементы слоя успешно обновлены';
          await this.updateMany({ ...params, id: this.itemId });
        } else {
          message = 'Элемент слоя успешно создан';
          const data = await this.$store.dispatch('layer-objects/geometries/create', params);
          this.itemId = data.id;
        }

        this.showNotify({ message });
      } catch (e) {
        const message = e.response?.data?.data?.message || e.message;
        this.showNotify({ type: 'error', message });
        console.error(e);
      }

      this.$emit('created');
    },

    async uploadAttachments() {
      try {
        if (this.attachmentsToUpload.length) {
          await this.uploadGeometryAttachment({
            layerObjectId: this.objectId,
            geometryId: this.itemId,
            files: this.attachmentsToUpload,
          });
          this.attachmentsToUpload = [];
          this.showNotify({ message: 'Файл успешно загружен' });
        }
      } catch (e) {
        throw new Error(e.response?.data?.data?.message || 'Ошибка при загрузке файла');
      }
    },

    async removeAttachment() {
      try {
        if (this.attachmentsToRemove.length) {
          for (let i = 0; i < this.attachmentsToRemove.length; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            await this.removeGeometryAttachment({
              layerObjectId: this.objectId,
              geometryId: this.itemId,
              id: this.attachmentsToRemove[i].attachmentId,
            });
          }
          this.attachmentsToRemove = [];
          this.showNotify({ message: 'Файл успешно удалён' });
        }
      } catch (e) {
        throw new Error(e.response?.data?.data?.message || 'Ошибка при удалении файла');
      }
    },

    async save() {
      this.isLoading = true;
      try {
        await this.updateObjectGeometry();
        await this.uploadAttachments();
        await this.removeAttachment();

        this.close();
        this.$emit('created');
      } catch (e) {
        const message = e.response?.data?.data?.message || e.message;
        this.showNotify({ type: 'error', message });
      } finally {
        this.isLoading = false;
      }
    },

    addAttachmentToRemove(index) {
      const toRemove = this.attachments.splice(index, 1);
      this.attachmentsToRemove = [...this.attachmentsToRemove, ...toRemove];
    },

    onPolygonEditorChange({ geometry }) {
      this.form.geometry = geometry;
    },

    newObj(obj) {
      return JSON.parse(JSON.stringify(obj));
    },
  },
};
</script>
